import {
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { PLAN_COLORS } from '../constants';
import type { PlanSummary } from '../types';

interface SummaryTabProps {
  summary: PlanSummary;
}

const headCellSx = {
  bgcolor: 'grey.50',
  fontSize: 11,
  fontWeight: 600,
  color: 'text.secondary',
  letterSpacing: '0.04em',
};

export const SummaryTab = ({ summary }: SummaryTabProps) => {
  const legend = [
    { color: PLAN_COLORS.orange, label: '% = your configured share (rebalances to 100%)' },
    { color: PLAN_COLORS.past, label: 'Past leads = % × total leads, loaded during ramp-up' },
    { color: PLAN_COLORS.newLeads, label: 'New/month = ongoing unsold leads (proportional)' },
    { color: PLAN_COLORS.newBatch, label: 'New/batch = per steady-state batch' },
  ];

  return (
    <Box>
      <TableContainer
        sx={{
          borderRadius: 1.5,
          boxShadow: '0 1px 3px rgba(0,0,0,.06)',
          bgcolor: 'background.paper',
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headCellSx}>#</TableCell>
              <TableCell sx={headCellSx}>Segment</TableCell>
              <TableCell sx={headCellSx}>% of pool</TableCell>
              <TableCell sx={headCellSx}>Past leads to load</TableCell>
              <TableCell sx={headCellSx}>New / month</TableCell>
              <TableCell sx={headCellSx}>New / batch</TableCell>
              <TableCell sx={headCellSx}>Loads from</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.segments.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell>
                  <Box
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 600,
                      px: 1,
                      py: 0.375,
                      borderRadius: 5,
                      bgcolor: PLAN_COLORS.orangeBg,
                      color: PLAN_COLORS.orange,
                    }}
                  >
                    #{s.priority}
                  </Box>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: PLAN_COLORS.orange,
                        fontSize: 14,
                      }}
                    >
                      {s.pctOfPool}%
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, s.pctOfPool)}
                      sx={{
                        width: 60,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { bgcolor: PLAN_COLORS.orange },
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    sx={{ fontFamily: 'monospace', fontWeight: 600, color: PLAN_COLORS.past }}
                  >
                    {s.pastToLoad.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    sx={{ fontFamily: 'monospace', fontWeight: 600, color: PLAN_COLORS.newLeads }}
                  >
                    {s.newPerMonth}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    component="span"
                    sx={{ fontFamily: 'monospace', fontWeight: 600, color: PLAN_COLORS.newBatch }}
                  >
                    {s.newPerBatch}
                  </Typography>
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>
                  day {s.startDay}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.75 }}>
        {legend.map((l) => (
          <Box key={l.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: l.color }} />
            <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{l.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
